import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, ArrowDownToLine, Upload,
} from 'lucide-react';

import { cardSettlementApi } from '@/api/cardSettlement.api';
import { CardSettlementImportPanel } from '@/components/banking/CardSettlementImportPanel';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner }            from '@/components/ui/Spinner';
import { ListPager } from '@/components/lists/ListPager';
import { ListSearch, DateRangeFilter } from '@/components/lists/ListFilters';
import { cn }                 from '@/utils/cn';
import { AppRoutes }          from '@/constants/routes';

import type {
  CardSettlementBatchListDto,
  CardSettlementBatchFilterDto,
} from '@/types/cardReconciliation.types';

const IMPORT_STATUS_COLORS: Record<string, string> = {
  Pending:    'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  Processing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  Completed:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Failed:     'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
};

export const CardSettlementListPage = () => {
  const navigate = useNavigate();

  const [items,   setItems]   = useState<CardSettlementBatchListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 50;

  const { hasPermission } = usePermission();
  const canImport = hasPermission(Permissions.CARD_SETTLEMENT_IMPORT);
  const [showImport, setShowImport] = useState(false);

  const [search,   setSearch]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const load = useCallback(async (filter: CardSettlementBatchFilterDto) => {
    setLoading(true);
    try {
      const res  = await cardSettlementApi.getList(filter);
      const data = res.data?.data;
      if (data) {
        setItems(data.items ?? []);
        setTotal(data.totalCount ?? 0);
      }
    } catch (err) {
      console.error('Failed to load settlement batches', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ search: search || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, page, pageSize });
  }, [page]); // eslint-disable-line

  const handleSearch = () => {
    setPage(1);
    load({ search: search || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, page: 1, pageSize });
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Card Settlement Import</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{total.toLocaleString()} batches</p>
          </div>
          <div className="flex items-center gap-2">
            {canImport && (
              <button
                onClick={() => setShowImport(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg"
              >
                <Upload size={14} /> Import file
              </button>
            )}
            <button onClick={() => load({ page, pageSize })} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400" title="Refresh">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {showImport && (
          <div className="mt-4">
            <CardSettlementImportPanel
              onClose={() => setShowImport(false)}
              onImported={() => load({ page: 1, pageSize })}
            />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <ListSearch
            value={search}
            onChange={setSearch}
            onSubmit={handleSearch}
            placeholder="Search batch #, file reference…"
          />
          <div className="flex items-center gap-2 ml-auto">
            <DateRangeFilter
              label="Sale date"
              from={dateFrom || undefined}
              to={dateTo || undefined}
              onChange={({ from, to }) => { setDateFrom(from ?? ''); setDateTo(to ?? ''); }}
            />
            <button onClick={handleSearch} className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg">Search</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <ArrowDownToLine size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No settlement batches found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Batch #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Acquirer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Import Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">File Reference</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Lines</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Gross Amount</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Net Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(batch => (
                <tr
                  key={batch.id}
                  onClick={() => navigate(AppRoutes.CARD_SETTLEMENT_DETAIL.replace(':id', batch.id))}
                  className="hover:bg-teal-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400">{batch.settlementBatchNo}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{batch.acquirerName}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{fmtDate(batch.importDate)}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">{batch.fileReference ?? '—'}</td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400">{batch.totalLines}</td>
                  <td className="px-4 py-3 text-right font-mono">{batch.totalGrossAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-right font-mono">{batch.totalNetAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', IMPORT_STATUS_COLORS[batch.importStatus] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400')}>
                      {batch.importStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={setPage}
          noun="settlements"
        />
      )}
    </div>
  );
};
